import { WORKSPACE_HEADER } from '../../shared/workspaces.ts';
import { setOfflineAccountScope } from '../offline/account-scope.ts';

export const AUTH_UNAUTHORIZED_EVENT = 'menu-lab:auth-unauthorized';
export const AUTH_ACCESS_CHANGED_EVENT = 'menu-lab:access-changed';
export const ACTIVE_WORKSPACE_STORAGE_KEY = 'menu_lab_workspace_id';

const MEMORY_READ_PREFIXES = [
  '/api/ingredients',
  '/api/recipes',
  '/api/dishes',
  '/api/menus',
  '/api/costs',
  '/api/workbench',
  '/api/media',
  '/api/allergens',
  '/api/rnd',
  '/api/versioning',
  '/api/imports'
];
const MEMORY_READ_TTL_MS = 30_000;
interface MemoryReadEntry {
  expiresAt: number;
  payload: unknown;
}
const memoryReadCache = new Map<string, MemoryReadEntry>();
const inflightReads = new Map<string, Promise<unknown>>();
let memoryReadEpoch = 0;

export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string | null;
  constructor(status: number, code: string, message: string, requestId: string | null = null) {
    super(message); this.status = status; this.code = code; this.requestId = requestId;
  }
}

export function activeWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)?.trim() || null;
}

export function setActiveWorkspaceId(workspaceId: string | null): void {
  if (typeof window === 'undefined') return;
  if (workspaceId) window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, workspaceId);
  else {
    window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
    invalidateMemoryReads();
  }
}

function requestMethod(init?: RequestInit): string {
  return (init?.method ?? 'GET').toUpperCase();
}

function pathnameFor(path: string): string {
  return new URL(path, 'https://menu-lab.local').pathname;
}

function isMemoryCacheableRead(path: string, init?: RequestInit): boolean {
  if (requestMethod(init) !== 'GET') return false;
  const pathname = pathnameFor(path);
  return MEMORY_READ_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function memoryReadKey(path: string, workspaceId: string | null): string {
  return `${workspaceId ?? 'workspace:none'}\0${path}`;
}

function invalidateMemoryReads(): void {
  memoryReadEpoch += 1;
  memoryReadCache.clear();
  inflightReads.clear();
}

export function clearMemoryReadCache(): void {
  invalidateMemoryReads();
}

export function primeMemoryRead<T>(path: string, payload: T, workspaceId = activeWorkspaceId()): void {
  if (!isMemoryCacheableRead(path)) return;
  const cacheKey = memoryReadKey(path, workspaceId);
  memoryReadCache.set(cacheKey, {
    expiresAt: Date.now() + MEMORY_READ_TTL_MS,
    payload
  });
}

function memberIdFrom(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const member = (body as { member?: unknown }).member;
  if (!member || typeof member !== 'object') return null;
  const id = (member as { id?: unknown }).id;
  return typeof id === 'string' && id.trim() ? id : null;
}

function rememberIdentity(path: string, body: unknown): void {
  const pathname = pathnameFor(path);
  if (pathname === '/api/auth/login' || pathname === '/api/auth/me') {
    const accountId = memberIdFrom(body);
    if (accountId) setOfflineAccountScope(accountId);
  } else if (pathname === '/api/auth/logout') {
    setOfflineAccountScope(null);
  }
}

type MemoryReadMode = 'default' | 'refresh';

async function requestJsonWithMemoryMode<T>(path: string, init: RequestInit | undefined, memoryReadMode: MemoryReadMode): Promise<T> {
  const method = requestMethod(init);
  const isJsonBody = typeof init?.body === 'string';
  const workspaceId = activeWorkspaceId();
  const memoryCacheable = isMemoryCacheableRead(path, init);
  const cacheKey = memoryReadKey(path, workspaceId);
  if (memoryCacheable) {
    if (memoryReadMode === 'default') {
      const cached = memoryReadCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) return cached.payload as T;
      if (cached) memoryReadCache.delete(cacheKey);
    }
    const inflight = inflightReads.get(cacheKey);
    if (inflight) return inflight as Promise<T>;
  }

  const epoch = memoryReadEpoch;
  const task = (async (): Promise<T> => {
    const response = await fetch(path, {
      credentials: 'same-origin', ...init,
      headers: {
        ...(isJsonBody ? { 'content-type': 'application/json' } : {}),
        ...(workspaceId ? { [WORKSPACE_HEADER]: workspaceId } : {}),
        ...(init?.headers ?? {})
      }
    });

    const body = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string }; requestId?: string } & T;
    if (!response.ok) {
      const code = body.error?.code ?? 'request_failed';
      const requestId = body.requestId?.trim() || response.headers.get('x-request-id')?.trim() || null;
      if (((response.status === 401 && code === 'unauthorized') || (response.status === 403 && code === 'member_disabled')) && typeof window !== 'undefined') {
        invalidateMemoryReads();
        setOfflineAccountScope(null);
        setOfflineAccountScope(null);
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
      } else if (response.status === 403 && ['forbidden', 'workspace_forbidden'].includes(code) && typeof window !== 'undefined') {
        invalidateMemoryReads();
        window.dispatchEvent(new Event(AUTH_ACCESS_CHANGED_EVENT));
      }
      throw new ApiError(response.status, code, body.error?.message ?? '请求失败', requestId);
    }

    rememberIdentity(path, body);

    if (method !== 'GET') {
      invalidateMemoryReads();
    } else if (memoryCacheable && epoch === memoryReadEpoch) {
      memoryReadCache.set(cacheKey, { expiresAt: Date.now() + MEMORY_READ_TTL_MS, payload: body });
    }
    return body as T;
  })();

  if (!memoryCacheable) return task;
  inflightReads.set(cacheKey, task as Promise<unknown>);
  try {
    return await task;
  } finally {
    if (inflightReads.get(cacheKey) === task) inflightReads.delete(cacheKey);
  }
}

export function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJsonWithMemoryMode<T>(path, init, 'default');
}

export function requestJsonFresh<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJsonWithMemoryMode<T>(path, init, 'refresh');
}


export interface ApiFileResponse {
  blob: Blob;
  contentType: string;
  contentDisposition: string | null;
}

export async function requestFile(path: string, init?: RequestInit): Promise<ApiFileResponse> {
  const workspaceId = activeWorkspaceId();
  const isJsonBody = typeof init?.body === 'string';
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(isJsonBody ? { 'content-type': 'application/json' } : {}),
      ...(workspaceId ? { [WORKSPACE_HEADER]: workspaceId } : {}),
      ...(init?.headers ?? {})
    }
  });
  if (!response.ok) {
    const body = await response.clone().json().catch(() => ({})) as {
      error?: { code?: string; message?: string; requestId?: string };
      requestId?: string;
    };
    const code = body.error?.code ?? 'request_failed';
    const requestId = body.error?.requestId?.trim() || body.requestId?.trim() || response.headers.get('x-request-id')?.trim() || null;
    if (((response.status === 401 && code === 'unauthorized') || (response.status === 403 && code === 'member_disabled')) && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
    } else if (response.status === 403 && ['forbidden', 'workspace_forbidden'].includes(code) && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_ACCESS_CHANGED_EVENT));
    }
    throw new ApiError(response.status, code, body.error?.message ?? '文件读取失败', requestId);
  }
  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') ?? 'application/octet-stream',
    contentDisposition: response.headers.get('content-disposition')
  };
}

export function jsonBody(method: 'POST' | 'PUT' | 'PATCH', body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}
